import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useSelector } from "react-redux";
import { formOpenSelector, setFormOpen } from "../redux/rhapsodyReducer";
import { useDispatch } from "react-redux";
import { parseWorkSessionIDs } from "../helpers";
import { MissionMap } from "../entities/missionMap";
import { useMissionMap } from "../templates/projectInvite";
import { useState } from "react";
import { useReleaseRequestMutation } from "../redux/missionMap";

/**
 *
 * @returns {ReactElement} AskForRelease page
 */
export function AskForRelease({
  token,
  season,
  mission: given,
}: {
  token: string;
  season?: boolean;
  mission?: MissionMap;
}) {
  const open = useSelector(formOpenSelector("askForRelease"));
  // A caller that already holds the mapping hands it over: inside a season the
  // projects arrive with the season, and asking the cache for a token nobody
  // fetched would put one request per project on the wire to learn what is
  // already in memory. The token still travels for the callers that only have
  // that (the classic call renders from the same query this would hit).
  const fetched = useMissionMap(given ? "" : token);
  const mission = given ?? fetched;
  const [releaseRequest, { isLoading }] = useReleaseRequestMutation();
  const [agreed, setAgreed] = useState(false);
  const [workSessionIDs, setWorkSessionIDs] = useState<number[]>([]);
  const [explanation, setExplanation] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const Interactors = mission?.Interactors;

  const close = () => {
    setSubmitted(false);
    setWorkSessionIDs([]);
    setExplanation("");
    setError("");
    dispatch(setFormOpen({ formID: "askForRelease", isOpen: false }));
  };

  const submit = async () => {
    setError("");
    try {
      await releaseRequest({
        publicToken: mission?.PublicToken,
        releaseWorkSessionIDs: JSON.stringify(workSessionIDs),
        releaseExplanation: explanation,
      }).unwrap();
      setSubmitted(true);
    } catch (e) {
      // Keep the form open so the musician can retry. The store wires
      // networkMiddleware only, not the RTK Query error bridge, so nothing
      // else would tell them the request failed.
      setError("We couldn't send your request. Please try again.");
    }
  };

  const releaseWS = parseWorkSessionIDs(mission?.ReleaseWorkSessionIDs);

  return (
    <>
      {(!Interactors || Interactors.length === 0) &&
      mission?.EnableRelease &&
      !mission?.CurrentStage?.Terminus &&
      !season ? (
        <Typography
          level="body-xs"
          sx={{
            "&:hover": {
              cursor: "pointer",
              textDecoration: "underline",
            },
          }}
          onClick={() =>
            dispatch(setFormOpen({ isOpen: true, formID: "askForRelease" }))
          }
          startDecorator={<i className="fa-solid fa-person-from-portal"></i>}
        >
          Ask to be released
        </Typography>
      ) : (
        []
      )}
      {releaseWS?.length ? (
        <Alert color="primary" size="sm">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            You asked for a release on {releaseWS?.length} Work Session.
            {releaseWS?.length > 1 ? "s" : ""}
            {mission?.ReleaseExplanation ? (
              <Chip size="sm" color="primary" variant="solid">
                {mission?.ReleaseExplanation}
              </Chip>
            ) : (
              []
            )}
          </Box>
        </Alert>
      ) : (
        []
      )}
      <Dialog open={open} onClose={close} sx={{ zIndex: 100003 }}>
        <DialogTitle>
          <Typography
            level="title-lg"
            startDecorator={<i className="fa-solid fa-person-from-portal"></i>}
          >
            Release Request
          </Typography>
        </DialogTitle>
        <Divider />
        {submitted ? (
          <>
            <DialogContent
              sx={{
                background: "#eceff1",
                gap: 1,
                display: "flex",
                flexDirection: "column",
                textAlign: "center",
              }}
            >
              <Typography level="h1" color="success">
                <i className="fa-regular fa-circle-check"></i>
              </Typography>
              <Typography level="title-lg">We have your request.</Typography>
              <Typography level="body-md">
                A {mission?.CompanyName} admin will contact you shortly.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button color="neutral" size="sm" onClick={close}>
                Close
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent
              sx={{
                background: "#eceff1",
                gap: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {error ? (
                <Alert color="danger" size="sm">
                  {error}
                </Alert>
              ) : null}
              <Textarea
                variant="outlined"
                autoFocus
                endDecorator={
                  <Typography
                    color={!explanation ? "danger" : "neutral"}
                    level="body-xs"
                  >
                    Required
                  </Typography>
                }
                sx={{ minHeight: 100 }}
                color={!explanation ? "danger" : "neutral"}
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                startDecorator="Explanation:"
                placeholder="Reason of the release"
              />
              <Divider>
                <i
                  style={{ marginRight: "4px" }}
                  className="fa-solid fa-arrow-down"
                ></i>
                Check the date(s) you wish to be released
                <i
                  style={{ marginLeft: "4px" }}
                  className="fa-solid fa-arrow-down"
                ></i>
              </Divider>
              {mission?.Engagements?.map((e) => {
                return e.Content.filter((e) => e.Playing).map((c) => (
                  <Box key={c.Title}>
                    <Checkbox
                      checked={workSessionIDs.includes(c.WorkSessionID)}
                      onChange={(e) => {
                        const _workSessionIDs = [...workSessionIDs].filter(
                          (e) => e !== c.WorkSessionID
                        );
                        if (e.target.checked) {
                          setWorkSessionIDs([
                            ..._workSessionIDs,
                            c.WorkSessionID,
                          ]);
                        } else {
                          setWorkSessionIDs(_workSessionIDs);
                        }
                      }}
                      label={
                        <span>
                          {c.Title}
                          <br />
                          <Typography level="body-sm">
                            {c.WeekDay} {c.Day} {c.Month} {c.HourRange}
                          </Typography>
                        </span>
                      }
                      key={c.Title}
                    />
                  </Box>
                ));
              })}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                size="sm"
                label={`I understand that this doesn't guarantee the release and I
            will need a confirmation from a ${mission?.CompanyName} admin.`}
              />
              <Button
                disabled={
                  !agreed ||
                  workSessionIDs.length === 0 ||
                  !explanation ||
                  isLoading ||
                  submitted
                }
                loading={isLoading}
                onClick={submit}
              >
                Submit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
